import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ExternalDataService, Customer, CustomerAddress, RecurringAppointmentSchedule, RecurringInfo } from '../../services/externalDataService.service';

@Component({
  selector: 'app-recurring-schedules',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './recurring-schedules.component.html',
  styleUrls: ['./recurring-schedules.component.css']
})
export class RecurringSchedulesComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  schedules: RecurringAppointmentSchedule[] = [];
  loadingSchedules = false;
  saving = false;
  errorMessage = '';
  successMessage = '';

  searchTerm = '';
  customers: Customer[] = [];
  selectedCustomer: Customer | null = null;
  addressOptions: CustomerAddress[] = [];

  frequencyOptions = [
    { value: 1, label: 'Daily' },
    { value: 2, label: 'Weekly' },
    { value: 3, label: 'Monthly' }
  ];

  dayOptions = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<string>();

  constructor(private fb: FormBuilder, private externalDataService: ExternalDataService) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadActiveSchedules();
    this.setupSearchSubscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupSearchSubscription(): void {
    this.searchSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.searchCustomers();
      });
  }

  onSearchInput(): void {
    this.searchSubject$.next(this.searchTerm);
  }

  buildForm(): void {
    this.form = this.fb.group({
      customerId: [''],
      name: [''],
      phoneNumber: [''],
      addressId: [''],
      frequency: [2, Validators.required],
      daysOfWeek: [[]],
      monthlyDays: [[]],
      endDate: ['', Validators.required],
      isActive: [true]
    });
  }

  loadActiveSchedules(): void {
    this.loadingSchedules = true;
    this.externalDataService.getRecurringSchedulesActive()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items) => {
          this.schedules = items || [];
          this.loadingSchedules = false;
        },
        error: () => {
          this.errorMessage = 'Unable to load recurring schedules.';
          this.loadingSchedules = false;
        }
      });
  }

  searchCustomers(): void {
    if (!this.searchTerm.trim()) {
      this.customers = [];
      return;
    }

    const searchTerm = this.searchTerm.trim();
    const filters: { name?: string; phoneNumber?: string } = {};

    // Check if search term contains only digits (phone number) or letters/spaces (name)
    const isNumeric = /^\d+$/.test(searchTerm);

    if (isNumeric) {
      filters.phoneNumber = searchTerm;
    } else {
      filters.name = searchTerm;
    }

    this.externalDataService.getCustomers(0, 20, filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.customers = response.items || [];
        },
        error: () => {
          this.customers = [];
        }
      });
  }

  selectCustomer(customer: Customer): void {
    this.selectedCustomer = customer;
    this.addressOptions = customer.customerAddress || [];
    this.form.patchValue({
      customerId: customer.customerId,
      name: customer.name,
      phoneNumber: customer.phoneNumber,
      addressId: this.addressOptions[0]?.addressId || ''
    });
    this.loadSchedulesForCustomer(customer.customerId);
  }

  selectAddress(address: CustomerAddress): void {
    this.form.patchValue({ addressId: address.addressId });
  }

  isWeekly(): boolean {
    return Number(this.form.get('frequency')?.value) === 2;
  }

  isMonthly(): boolean {
    return Number(this.form.get('frequency')?.value) === 3;
  }

  toggleDay(day: number): void {
    const current = [...(this.form.get('daysOfWeek')?.value || [])];
    const idx = current.indexOf(day);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(day);
    }
    this.form.patchValue({ daysOfWeek: current.sort((a, b) => a - b) });
  }

  toggleMonthDay(day: number): void {
    const current = [...(this.form.get('monthlyDays')?.value || [])];
    const idx = current.indexOf(day);
    if (idx > -1) {
      current.splice(idx, 1);
    } else {
      current.push(day);
    }
    this.form.patchValue({ monthlyDays: current.sort((a, b) => a - b) });
  }

  submit(): void {
    // Manual validation since we removed Angular validators for customer fields
    const { customerId, name, phoneNumber, addressId, frequency, endDate } = this.form.value;

    if (!customerId || customerId.trim() === '') {
      this.errorMessage = 'Please select a customer.';
      return;
    }

    if (!name || name.trim() === '') {
      this.errorMessage = 'Selected customer must have a valid name.';
      return;
    }

    if (!phoneNumber || phoneNumber.trim() === '') {
      this.errorMessage = 'Selected customer must have a valid phone number.';
      return;
    }

    if (!addressId || addressId.trim() === '') {
      this.errorMessage = 'Please select an address.';
      this.form.get('addressId')?.markAsTouched();
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { daysOfWeek, monthlyDays } = this.form.value;

    // Additional validation for required IDs
    if (!customerId || customerId.trim() === '') {
      this.errorMessage = 'Please select a customer.';
      return;
    }

    if (!addressId || addressId.trim() === '') {
      this.errorMessage = 'Please select an address.';
      this.form.get('addressId')?.markAsTouched();
      return;
    }

    // Validate that selected customer has required information
    if (!name || name.trim() === '') {
      this.errorMessage = 'Selected customer must have a valid name.';
      return;
    }

    if (!phoneNumber || phoneNumber.trim() === '') {
      this.errorMessage = 'Selected customer must have a valid phone number.';
      return;
    }

    const recurringInfo: RecurringInfo = {
      frequency: Number(frequency), // Convert to number (HTML select returns string)
      daysOfWeek: this.isWeekly() ? daysOfWeek || [] : undefined,
      monthlyDays: this.isMonthly() ? monthlyDays || [] : undefined,
      endDate: endDate ? new Date(endDate).toISOString() : undefined
    };

    const payload = {
      customerId,
      addressId,
      recurringInfo,
      name: name ? name.trim() : '',
      phoneNumber: phoneNumber ? phoneNumber.trim() : ''
    };

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.externalDataService.createRecurringSchedule(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (schedule) => {
          this.successMessage = 'Recurring schedule created successfully.';
          this.saving = false;
          this.form.patchValue({ endDate: '', daysOfWeek: [], monthlyDays: [] });
          if (this.selectedCustomer && schedule.customerId === this.selectedCustomer.customerId) {
            this.loadSchedulesForCustomer(this.selectedCustomer.customerId);
          } else {
            this.loadActiveSchedules();
          }
        },
        error: (error) => {
          console.error('API Error:', error);
          this.errorMessage = error.error?.message || 'Failed to create schedule.';
          this.saving = false;
        }
      });
  }

  pause(schedule: RecurringAppointmentSchedule): void {
    this.externalDataService.pauseRecurringSchedule(schedule.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.refreshSchedules(),
        error: () => this.errorMessage = 'Failed to pause schedule.'
      });
  }

  resume(schedule: RecurringAppointmentSchedule): void {
    this.externalDataService.resumeRecurringSchedule(schedule.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.refreshSchedules(),
        error: () => this.errorMessage = 'Failed to resume schedule.'
      });
  }

  remove(schedule: RecurringAppointmentSchedule): void {
    if (!confirm('Delete this recurring schedule?')) {
      return;
    }
    this.externalDataService.deleteRecurringSchedule(schedule.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.refreshSchedules(),
        error: () => this.errorMessage = 'Failed to delete schedule.'
      });
  }

  private loadSchedulesForCustomer(customerId: string): void {
    this.loadingSchedules = true;
    this.externalDataService.getRecurringSchedulesByCustomer(customerId, 0, 50)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const items = (response as any)?.items || response || [];
          this.schedules = items;          
          this.loadingSchedules = false;
        },
        error: () => {
          this.loadingSchedules = false;
          this.errorMessage = 'Unable to load customer schedules.';
        }
      });
  }

  refreshSchedules(): void {
    if (this.selectedCustomer) {
      this.loadSchedulesForCustomer(this.selectedCustomer.customerId);
    } else {
      this.loadActiveSchedules();
    }
  }

  isCustomerValidForSubmission(): boolean {
    const name = this.form.get('name')?.value;
    const phoneNumber = this.form.get('phoneNumber')?.value;
    return !!(name?.trim() && phoneNumber?.trim());
  }
}


